import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, Package, Wrench, GraduationCap, Search, X } from "lucide-react";

/**
 * Generic multi-select dropdown for any catalogue endpoint that returns
 * an array of `{id, name, category|type}`. Powers the product picker on
 * deals/quotations AND the services + training pickers on Convert-Lead
 * and Service-Execution flows.
 *
 * Props:
 *   value         — array of selected ids
 *   onChange      — (ids: string[]) => void
 *   endpoint      — REST path under /api (default "/products")
 *   typeFilter    — optional `type` value to narrow down service results
 *                   (e.g. "training" to show only training services)
 *   labelKey      — field used as display name (default "name")
 *   categoryKey   — field used as subtext (default "category" for products,
 *                   "type" for services)
 *   icon          — lucide-react icon shown next to placeholder
 *   placeholder   — placeholder text
 *   searchPlaceholder
 *   emptyText
 *   testId
 *   disabled
 */
export default function MultiSelectCatalog({
  value = [],
  onChange,
  endpoint = "/products",
  typeFilter = null,
  excludeTypeFilter = null,
  labelKey = "name",
  categoryKey = "category",
  icon: Icon = Package,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No items in catalog yet.",
  testId = "multi-select-catalog",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(endpoint)
      .then((r) => {
        if (cancelled) return;
        let arr = r.data || [];
        // `type` filter — case-insensitive substring match so "training",
        // "Training & Workshop", "Online Training" all match for the
        // training pipeline.
        if (typeFilter) {
          const f = String(typeFilter).toLowerCase();
          arr = arr.filter((it) => String(it.type || it.category || "").toLowerCase().includes(f));
        }
        // Inverse filter — used by the Technical Services picker to hide
        // training entries (they have their own picker on Convert Lead).
        if (excludeTypeFilter) {
          const f = String(excludeTypeFilter).toLowerCase();
          arr = arr.filter((it) => !String(it.type || it.category || "").toLowerCase().includes(f));
        }
        setItems(arr);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, typeFilter, excludeTypeFilter]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const sorted = [...items].sort((a, b) => (a[labelKey] || "").localeCompare(b[labelKey] || ""));
    if (!q.trim()) return sorted;
    const needle = q.toLowerCase();
    return sorted.filter(
      (p) =>
        (p[labelKey] || "").toLowerCase().includes(needle) ||
        (p[categoryKey] || "").toLowerCase().includes(needle) ||
        (p.sku || "").toLowerCase().includes(needle)
    );
  }, [q, items, labelKey, categoryKey]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onChange(value.filter((v) => v !== id));
  };

  const selectedItems = useMemo(
    () => items.filter((p) => selectedSet.has(p.id)),
    [items, selectedSet]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between rounded-2xl h-auto min-h-11 px-3 py-2 mt-1.5 font-normal hover:bg-muted/40"
          data-testid={testId}
        >
          <div className="flex flex-wrap gap-1.5 flex-1 items-center">
            {selectedItems.length === 0 ? (
              <span className="text-muted-foreground inline-flex items-center gap-2">
                <Icon className="h-4 w-4" /> {placeholder}
              </span>
            ) : (
              selectedItems.map((p) => (
                <Badge
                  key={p.id}
                  variant="outline"
                  className="bg-primary-50 text-primary-700 border-primary-200 gap-1 pr-1 max-w-full"
                  data-testid={`${testId}-chip-${p.id}`}
                >
                  <span className="truncate max-w-[14rem]">{p[labelKey]}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => remove(p.id, e)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") remove(p.id, e); }}
                    className="hover:bg-primary-100 rounded-full p-0.5 -mr-0.5 cursor-pointer"
                    aria-label={`Remove ${p[labelKey]}`}
                    data-testid={`${testId}-remove-${p.id}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 rounded-2xl"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="p-2 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 h-9 rounded-xl bg-muted/40 border-0"
              data-testid={`${testId}-search`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto overscroll-contain">
          <div className="p-1">
            {loading ? (
              <p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                {items.length === 0 ? emptyText : "No matches."}
              </p>
            ) : (
              filtered.map((p) => {
                const checked = selectedSet.has(p.id);
                const sub = p[categoryKey];
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`flex items-start gap-2.5 w-full px-2.5 py-2 rounded-xl tap text-left transition ${
                      checked ? "bg-primary-50/60" : "hover:bg-muted/50"
                    }`}
                    data-testid={`${testId}-option-${p.id}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 h-4 w-4 rounded border grid place-items-center shrink-0 ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-input bg-background"
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{p[labelKey]}</p>
                      {sub && (
                        <p className="text-[11px] text-muted-foreground truncate">{sub}</p>
                      )}
                    </div>
                    {checked && <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
        {selectedItems.length > 0 && (
          <div className="px-3 py-2 border-t border-border/60 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              {selectedItems.length} selected
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange([])}
              className="h-7 px-2 text-xs rounded-xl"
              data-testid={`${testId}-clear`}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Convenience pre-bound flavours so call sites stay readable.
export function MultiSelectServices(props) {
  return (
    <MultiSelectCatalog
      endpoint="/services"
      // Hide training rows so the Technical Services picker on Convert-Lead
      // shows only inspection / technical / calibration / AMC services.
      // Trainings have their own dedicated picker (MultiSelectTrainings).
      excludeTypeFilter="training"
      categoryKey="type"
      icon={Wrench}
      placeholder="Select one or more technical services…"
      searchPlaceholder="Search services…"
      emptyText="No technical services in catalog yet."
      testId="multi-select-services"
      {...props}
    />
  );
}

export function MultiSelectTrainings(props) {
  return (
    <MultiSelectCatalog
      endpoint="/services"
      typeFilter="training"
      categoryKey="type"
      icon={GraduationCap}
      placeholder="Select one or more trainings…"
      searchPlaceholder="Search trainings…"
      emptyText="No trainings in catalog yet."
      testId="multi-select-trainings"
      {...props}
    />
  );
}
